import React, { useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  X,
  Printer,
  Brain,
  Phone,
  Calendar,
  User,
  Shield,
  FileText,
  Building2,
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import clsx from 'clsx';
import type { Conversation } from '../../stores/whatsapp/chatStore';
import { useMessages } from '../../hooks/whatsapp/useChat';
import { useKanbanStore } from '../../stores/whatsapp/kanbanStore';
import { formatPhone, parseUTC } from '../../services/whatsapp/waFormatters';
import { PRIORITIES } from '../../services/whatsapp/waConstants';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  conversation: Conversation;
  protocol: string;
}

// Os timestamps do backend vem em UTC (sem 'Z'); parseUTC converte para o fuso
// local na exibicao — alinhado a lista do Historico (evita off-by-3h).
function fmtTime(d: string) {
  return format(parseUTC(d), 'HH:mm');
}
function fmtDate(d: string) {
  return format(parseUTC(d), "dd/MM/yyyy 'as' HH:mm", { locale: ptBR });
}

export default function ConversationViewModal({ isOpen, onClose, conversation, protocol }: Props) {
  const { data: messages } = useMessages(conversation.id);
  const kanbanTicket = useKanbanStore(s => s.getByConversation(conversation.id));
  const printRef = useRef<HTMLDivElement>(null);
  const priority = PRIORITIES[conversation.priority];
  const aiSummary = kanbanTicket?.aiSummary || null;
  const logoUrl = `${window.location.origin}/logo-especialistas.png`;

  // Detecta departamento do ticket, assunto ou contexto
  const detectDept = (): string => {
    if (kanbanTicket?.category) return kanbanTicket.category;
    const text = `${conversation.subject || ''} ${conversation.last_message || ''}`.toLowerCase();
    if (
      text.includes('irpf') ||
      text.includes('imposto') ||
      text.includes('fiscal') ||
      text.includes('nota') ||
      text.includes('cnd')
    )
      return 'Fiscal';
    if (
      text.includes('folha') ||
      text.includes('ferias') ||
      text.includes('rescisao') ||
      text.includes('admissao')
    )
      return 'Depto. Pessoal';
    if (text.includes('balancete') || text.includes('contabil') || text.includes('dre'))
      return 'Contabil';
    if (
      text.includes('abertura') ||
      text.includes('societario') ||
      text.includes('contrato') ||
      text.includes('holding')
    )
      return 'Societario';
    return 'Atendimento';
  };
  const dept = detectDept();
  const agentLabel = conversation.assigned_agent_name
    ? `${conversation.assigned_agent_name} — ${dept}`
    : 'Nao atribuido';

  const handlePrint = async () => {
    // Converte logo para base64 para funcionar no PDF
    let logoBase64 = '';
    try {
      const resp = await fetch(logoUrl);
      const blob = await resp.blob();
      logoBase64 = await new Promise<string>(resolve => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.readAsDataURL(blob);
      });
    } catch {
      /* logo nao carregou */
    }

    const messagesHtml =
      (printRef.current?.querySelector('.messages') as HTMLElement)?.innerHTML || '';

    const escapeHtml = (str: string) =>
      str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    const summaryHtml = aiSummary
      ? `
      <div class="summary-box">
        <h3>Resumo gerado por IA</h3>
        <p>${escapeHtml(aiSummary).replace(/\n/g, '<br>')}</p>
      </div>`
      : '';

    let statusLabel = 'Encerrado';
    if (conversation.status === 'open') statusLabel = 'Aberto';
    else if (conversation.status === 'waiting') statusLabel = 'Aguardando';
    const now = new Date().toLocaleDateString('pt-BR');

    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(`<!DOCTYPE html><html><head><title>Protocolo ${protocol}</title>
<style>
*{margin:0;padding:0;box-sizing:border-box}
body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;color:#1a1a1a;font-size:13px}
.page{max-width:700px;margin:0 auto;padding:24px 32px}
.header{background:linear-gradient(135deg,#075E54 0%,#128C7E 50%,#25D366 100%);padding:24px 28px;color:#fff;display:flex;align-items:center;justify-content:space-between;border-radius:12px;margin-bottom:20px}
.header-left{display:flex;align-items:center;gap:16px}
.header img{height:40px}
.header .title{font-size:20px;font-weight:800;letter-spacing:-0.5px}
.header .subtitle{font-size:11px;opacity:0.8;margin-top:2px}
.header .proto-badge{font-size:13px;font-weight:700;background:rgba(255,255,255,0.2);padding:6px 16px;border-radius:20px;backdrop-filter:blur(4px)}
.info-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:20px}
.info-card{background:#f8f9fa;border:1px solid #e9ecef;border-radius:8px;padding:10px 14px}
.info-card .label{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.8px;color:#868e96;margin-bottom:3px}
.info-card .value{font-size:13px;color:#212529;font-weight:500}
.info-card.full{grid-column:1/-1}
.divider{height:1px;background:#e9ecef;margin:16px 0}
.summary-box{background:linear-gradient(135deg,#f5f3ff,#ede9fe);border:1px solid #d8b4fe;border-radius:10px;padding:16px 18px;margin-bottom:20px}
.summary-box h3{font-size:13px;color:#6d28d9;margin-bottom:8px;font-weight:700;display:flex;align-items:center;gap:6px}
.summary-box p{font-size:12px;color:#4c1d95;white-space:pre-wrap;line-height:1.7}
.messages{margin-top:8px}
.msg{margin-bottom:6px;padding:10px 14px;border-radius:10px;max-width:72%;font-size:12px;line-height:1.6}
.msg-client{background:#f1f3f5;margin-right:auto;border-bottom-left-radius:3px}
.msg-agent{background:#d4edda;margin-left:auto;border-bottom-right-radius:3px}
.msg-bot{background:#c3e6cb;margin-left:auto;border-left:3px solid #28a745}
.msg-note{background:#fff3cd;border:1px solid #ffc107;margin-left:auto}
.msg-ai{background:#ede9fe;border:1px solid #c084fc;margin:10px auto;max-width:88%}
.msg-system{text-align:center;margin:8px 0;font-size:11px;color:#868e96;font-style:italic}
.msg-divider{display:flex;align-items:center;gap:8px;margin:14px 0}
.msg-divider .line{flex:1;height:1px}
.msg-divider .label{font-size:10px;font-weight:700;padding:4px 14px;border-radius:20px;white-space:nowrap}
.msg-divider.start .line{background:#28a745}
.msg-divider.start .label{color:#155724;background:#d4edda;border:1px solid #c3e6cb}
.msg-divider.end .line{background:#adb5bd}
.msg-divider.end .label{color:#495057;background:#e9ecef;border:1px solid #dee2e6}
.msg-divider.transfer .line{background:#80bdff}
.msg-divider.transfer .label{color:#004085;background:#cce5ff;border:1px solid #b8daff}
.msg-time{font-size:9px;color:#adb5bd;text-align:right;margin-top:3px}
.msg-sender{font-size:10px;font-weight:600;color:#868e96;margin-bottom:2px}
.footer{margin-top:28px;padding-top:14px;border-top:2px solid #e9ecef;display:flex;align-items:center;justify-content:space-between}
.footer-left{font-size:10px;color:#adb5bd}
.footer-right{font-size:10px;color:#adb5bd}
@media print{.page{padding:16px 24px}}
</style></head><body>
<div class="page">
  <div class="header">
    <div class="header-left">
      ${logoBase64 ? `<img src="${logoBase64}" alt="Logo">` : ''}
      <div>
        <div class="title">Rede Especialistas</div>
        <div class="subtitle">Registro de Atendimento WhatsApp</div>
      </div>
    </div>
    <div class="proto-badge">${protocol}</div>
  </div>

  <div class="info-grid">
    <div class="info-card">
      <div class="label">Cliente</div>
      <div class="value">${conversation.client_name}</div>
    </div>
    <div class="info-card">
      <div class="label">Telefone</div>
      <div class="value">${formatPhone(conversation.client_phone)}</div>
    </div>
    <div class="info-card">
      <div class="label">Atendente / Departamento</div>
      <div class="value">${agentLabel}</div>
    </div>
    <div class="info-card">
      <div class="label">Status / Prioridade</div>
      <div class="value">${statusLabel} — ${priority?.label || conversation.priority}</div>
    </div>
    <div class="info-card">
      <div class="label">Data de Inicio</div>
      <div class="value">${fmtDate(conversation.started_at)}</div>
    </div>
    ${conversation.subject ? `<div class="info-card"><div class="label">Assunto</div><div class="value">${conversation.subject}</div></div>` : ''}
    ${conversation.client_company ? `<div class="info-card full"><div class="label">Empresa</div><div class="value">${conversation.client_company}</div></div>` : ''}
  </div>

  ${summaryHtml}

  <div class="divider"></div>
  <div class="messages">${messagesHtml}</div>

  <div class="footer">
    <div class="footer-left">Protocolo: ${protocol} — Gerado em ${now}</div>
    <div class="footer-right">Rede Especialistas — Documento Interno</div>
  </div>
</div>
<script>
  window.onafterprint = function() { window.close(); };
</script>
</body></html>`);
    win.document.close();
    let printed = false;
    const doPrint = () => {
      if (printed) return;
      printed = true;
      win.focus();
      win.print();
      // Fecha a aba apos imprimir/cancelar
      win.onafterprint = () => win.close();
      // Fallback: se onafterprint nao disparar, fecha depois de 1s apos o dialogo fechar
      const checkClosed = setInterval(() => {
        try {
          if (win.document.hasFocus() || !win.closed) {
            // Dialogo de impressao fechou, espera um pouco e fecha
          }
        } catch {
          clearInterval(checkClosed);
        }
      }, 500);
      // Timeout final: fecha em 60s se nada acontecer
      setTimeout(() => {
        try {
          win.close();
        } catch {
          // ignore if window already closed
        }
      }, 60000);
    };
    win.onload = doPrint;
    setTimeout(doPrint, 600);
  };

  if (!isOpen) return null;

  const modal = (
    <div className="fixed inset-0 flex items-center justify-center" style={{ zIndex: 99999 }}>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div
        className="relative mx-4 flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        style={{ zIndex: 100000 }}
      >
        {/* Header */}
        <div className="flex items-center justify-between rounded-t-2xl border-b border-gray-100 bg-gradient-to-r from-[#075E54] to-[#25D366] px-6 py-4 text-white">
          <div>
            <div className="flex items-center gap-2">
              <span className="rounded bg-white/20 px-2.5 py-0.5 font-mono text-sm font-bold">
                {protocol}
              </span>
              <span
                className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${conversation.status === 'closed' ? 'bg-white/30' : 'bg-white/20'}`}
              >
                {/* eslint-disable-next-line no-nested-ternary */}
                {conversation.status === 'open'
                  ? 'Aberto'
                  : conversation.status === 'waiting'
                    ? 'Aguardando'
                    : 'Encerrado'}
              </span>
            </div>
            <p className="mt-0.5 text-sm text-white/80">
              {conversation.client_name}
              {dept && <span className="text-white/60"> — {dept}</span>}
              {conversation.assigned_agent_name && (
                <span className="text-white/50"> • {conversation.assigned_agent_name}</span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {aiSummary && (
              <button
                type="button"
                onClick={() =>
                  document
                    .getElementById('modal-ai-summary')
                    ?.scrollIntoView({ behavior: 'smooth' })
                }
                className="flex items-center gap-1.5 rounded-lg bg-white/20 px-3 py-1.5 text-xs font-semibold transition-colors hover:bg-white/30"
              >
                <Brain size={14} />
                Ler Resumo
              </button>
            )}
            <button
              type="button"
              onClick={handlePrint}
              className="flex items-center gap-1.5 rounded-lg bg-white/20 px-3 py-1.5 text-xs font-semibold transition-colors hover:bg-white/30"
            >
              <Printer size={14} />
              Imprimir PDF
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded-full p-2 transition-colors hover:bg-white/20"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div ref={printRef}>
            {/* Info */}
            <div className="mb-4 grid grid-cols-2 gap-3">
              <div className="flex items-center gap-2 rounded-xl bg-gray-50 p-2.5">
                <Phone size={14} className="text-gray-400" />
                <span className="text-xs text-gray-600">
                  {formatPhone(conversation.client_phone)}
                </span>
              </div>
              <div className="flex items-center gap-2 rounded-xl bg-gray-50 p-2.5">
                <Calendar size={14} className="text-gray-400" />
                <span className="text-xs text-gray-600">{fmtDate(conversation.started_at)}</span>
              </div>
              <div className="flex items-center gap-2 rounded-xl bg-gray-50 p-2.5">
                <User size={14} className="text-gray-400" />
                <span className="text-xs text-gray-600">{agentLabel}</span>
              </div>
              <div className="flex items-center gap-2 rounded-xl bg-gray-50 p-2.5">
                <Shield size={14} className="text-gray-400" />
                <span className={`text-xs font-semibold ${priority?.color || 'text-gray-600'}`}>
                  {priority?.label || conversation.priority}
                </span>
              </div>
              {conversation.client_company && (
                <div className="col-span-2 flex items-center gap-2 rounded-xl bg-gray-50 p-2.5">
                  <Building2 size={14} className="text-gray-400" />
                  <span className="text-xs text-gray-600">{conversation.client_company}</span>
                </div>
              )}
            </div>

            {/* AI Summary */}
            {aiSummary && (
              <div
                id="modal-ai-summary"
                className="summary-box mb-4 rounded-xl border border-violet-200 bg-violet-50 p-4"
              >
                <h3 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-violet-700">
                  <Brain size={15} className="text-violet-500" />
                  Resumo gerado por IA
                </h3>
                <p className="whitespace-pre-wrap text-sm leading-relaxed text-violet-900">
                  {aiSummary}
                </p>
              </div>
            )}

            {/* Messages */}
            <div className="messages space-y-1.5">
              {messages.map(msg => {
                const isStart =
                  msg.sender_type === 'system' && msg.content.includes('Atendimento iniciado');
                const isEnd =
                  msg.sender_type === 'system' && msg.content.includes('Chamado encerrado');
                const isTransfer =
                  msg.sender_type === 'system' && msg.content.includes('Transferido');
                const isAiResumo =
                  msg.sender_type === 'system' && msg.content.includes('Resumo IA');

                if (isStart || isEnd) {
                  return (
                    <div
                      key={msg.id}
                      className={clsx(
                        'msg-divider flex items-center gap-2 py-2',
                        isStart ? 'start' : 'end',
                      )}
                    >
                      <div
                        className={clsx('h-px flex-1', isStart ? 'bg-green-400' : 'bg-gray-300')}
                      />
                      <span
                        className={clsx(
                          'label rounded-full px-3 py-1 text-[11px] font-semibold',
                          isStart
                            ? 'border border-green-200 bg-green-50 text-green-700'
                            : 'border border-gray-200 bg-gray-100 text-gray-600',
                        )}
                      >
                        {isStart ? '▶' : '■'} {msg.content}
                      </span>
                      <div
                        className={clsx('h-px flex-1', isStart ? 'bg-green-400' : 'bg-gray-300')}
                      />
                    </div>
                  );
                }

                if (isAiResumo) {
                  return (
                    <div
                      key={msg.id}
                      className="msg-ai my-2 rounded-xl border border-violet-200 bg-violet-50 p-3"
                    >
                      <p className="mb-1 text-[10px] font-bold uppercase tracking-wider text-violet-500">
                        Resumo IA
                      </p>
                      <p className="whitespace-pre-wrap text-xs text-violet-900">
                        {msg.content.replace('📋 Resumo IA:\n', '')}
                      </p>
                      <p className="msg-time mt-1 text-right text-[10px] text-violet-400">
                        {fmtTime(msg.created_at)}
                      </p>
                    </div>
                  );
                }

                if (isTransfer) {
                  return (
                    <div
                      key={msg.id}
                      className="msg-divider transfer flex items-center gap-2 py-1.5"
                    >
                      <div className="line h-px flex-1 bg-blue-200" />
                      <span className="label rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-[10px] text-blue-600">
                        ↗ {msg.content}
                      </span>
                      <div className="line h-px flex-1 bg-blue-200" />
                    </div>
                  );
                }

                if (msg.sender_type === 'system') {
                  return (
                    <div key={msg.id} className="msg-system py-1 text-center">
                      <span className="rounded-full bg-gray-100 px-3 py-1 text-[11px] italic text-gray-500">
                        {msg.content}
                      </span>
                    </div>
                  );
                }

                if (msg.is_internal_note) {
                  return (
                    <div
                      key={msg.id}
                      className="msg msg-note ml-auto max-w-[75%] rounded-xl border border-amber-200 bg-amber-50 p-3"
                    >
                      <p className="msg-sender text-[10px] font-bold text-amber-500">
                        Nota interna
                      </p>
                      <p className="whitespace-pre-wrap text-sm text-amber-900">{msg.content}</p>
                      <p className="msg-time mt-1 text-right text-[10px] text-amber-400">
                        {fmtTime(msg.created_at)}
                      </p>
                    </div>
                  );
                }

                if (msg.sender_type === 'bot') {
                  return (
                    <div
                      key={msg.id}
                      className="msg msg-bot ml-auto max-w-[75%] rounded-xl border-l-[3px] border-green-500 bg-green-100 p-3"
                    >
                      <p className="msg-sender text-[10px] font-bold text-green-600">Automatica</p>
                      <p className="whitespace-pre-wrap text-sm text-green-900">{msg.content}</p>
                      <p className="msg-time mt-1 text-right text-[10px] text-green-500">
                        {fmtTime(msg.created_at)}
                      </p>
                    </div>
                  );
                }

                const isAgent = msg.sender_type === 'agent';
                return (
                  <div
                    key={msg.id}
                    className={clsx(
                      'msg max-w-[75%] rounded-xl p-3',
                      isAgent ? 'msg-agent ml-auto bg-green-50' : 'msg-client mr-auto bg-gray-50',
                    )}
                  >
                    <p className="msg-sender text-[10px] font-semibold text-gray-400">
                      {isAgent ? 'Atendente' : 'Cliente'}
                    </p>
                    <p className="whitespace-pre-wrap text-sm text-gray-900">{msg.content}</p>
                    <p
                      className={clsx(
                        'msg-time mt-1 text-right text-[10px]',
                        isAgent ? 'text-green-500' : 'text-gray-400',
                      )}
                    >
                      {fmtTime(msg.created_at)}
                    </p>
                  </div>
                );
              })}
              {messages.length === 0 && (
                <div className="py-8 text-center text-gray-400">
                  <FileText size={32} className="mx-auto mb-2 text-gray-300" />
                  <p className="text-sm">Nenhuma mensagem</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
